import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EVENT_TYPES = {
  party:       { label: 'Party',        icon: '🎉', color: '#ff6b35' },
  hackathon:   { label: 'Hackathon',    icon: '⚡', color: '#00d4ff' },
  townhall:    { label: 'Town Hall',    icon: '📢', color: '#a855f7' },
  celebration: { label: 'Celebration',  icon: '🏆', color: '#ffd700' },
  training:    { label: 'Training',     icon: '🎓', color: '#00ff88' },
  recognition: { label: 'Recognition',  icon: '🌟', color: '#ffd700' },
  event:       { label: 'Event',        icon: '📅', color: '#00d4ff' },
};
const ANN_CATS = {
  info:        { icon: 'ℹ', color: '#00d4ff' },
  urgent:      { icon: '🚨', color: '#ff6b35' },
  event:       { icon: '📅', color: '#a855f7' },
  achievement: { icon: '🏆', color: '#ffd700' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}
function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return formatDate(iso);
}

export default function Events() {
  const { user, can } = useAuth();
  const [tab, setTab] = useState('announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [members, setMembers] = useState([]);
  const [myStatus, setMyStatus] = useState({ canGive: true, gave: null });
  const [toast, setToast] = useState(null);

  // Announcement form
  const [showAnnForm, setShowAnnForm] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', body: '', category: 'info', pinned: false });
  const [editAnn, setEditAnn] = useState(null);

  // Event form
  const [showEvForm, setShowEvForm] = useState(false);
  const [evForm, setEvForm] = useState({ title: '', description: '', type: 'event', date: '', location: '' });

  // Recognition form
  const [showRecForm, setShowRecForm] = useState(false);
  const [recForm, setRecForm] = useState({ receiverId: '', message: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(() => {
    api.get('/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
    api.get('/events').then(r => setEvents(r.data)).catch(() => {});
    api.get('/recognitions').then(r => setRecognitions(r.data)).catch(() => {});
    api.get('/recognitions/my-status').then(r => setMyStatus(r.data)).catch(() => {});
    api.get('/users').then(r => setMembers((r.data || []).filter(u => u.id !== user?.id && u.role !== 'intern'))).catch(() => {});
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // ── Announcement actions ──
  const saveAnn = async () => {
    try {
      if (editAnn) {
        const { data } = await api.patch(`/announcements/${editAnn.id}`, annForm);
        setAnnouncements(p => p.map(a => a.id === data.id ? data : a));
      } else {
        const { data } = await api.post('/announcements', annForm);
        setAnnouncements(p => [data, ...p]);
      }
      setShowAnnForm(false); setEditAnn(null);
      setAnnForm({ title: '', body: '', category: 'info', pinned: false });
      showToast(editAnn ? 'Announcement updated' : 'Announcement posted');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };
  const deleteAnn = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      setAnnouncements(p => p.filter(a => a.id !== id));
      showToast('Deleted');
    } catch { showToast('Failed', 'error'); }
  };
  const togglePin = async (ann) => {
    const { data } = await api.patch(`/announcements/${ann.id}`, { pinned: !ann.pinned });
    setAnnouncements(p => p.map(a => a.id === data.id ? data : a));
  };

  // ── Event actions ──
  const saveEvent = async () => {
    try {
      const { data } = await api.post('/events', evForm);
      setEvents(p => [...p, data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      setShowEvForm(false);
      setEvForm({ title: '', description: '', type: 'event', date: '', location: '' });
      showToast('Event created!');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };
  const rsvp = async (id) => {
    try {
      const { data } = await api.post(`/events/${id}/rsvp`);
      setEvents(p => p.map(e => e.id === id ? data : e));
    } catch { showToast('RSVP failed', 'error'); }
  };
  const deleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      setEvents(p => p.filter(e => e.id !== id));
      showToast('Event deleted');
    } catch { showToast('Failed', 'error'); }
  };

  // ── Recognition actions ──
  const sendRecognition = async () => {
    if (!recForm.receiverId || !recForm.message.trim()) return showToast('Choose someone and write a message', 'error');
    try {
      const { data } = await api.post('/recognitions', recForm);
      setRecognitions(p => [data.recognition, ...p]);
      setMyStatus({ canGive: false, gave: data.recognition });
      setShowRecForm(false);
      setRecForm({ receiverId: '', message: '' });
      showToast(`🌟 Recognition sent! +${data.giverBonus} pts for you too!`, 'success');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const upcoming = events.filter(e => new Date(e.date) >= new Date());
  const past = events.filter(e => new Date(e.date) < new Date());

  const TABS = [
    { key: 'announcements', label: '📢 Announcements', count: announcements.filter(a => !a.pinned).length },
    { key: 'events', label: '🎉 Events & Parties', count: upcoming.length },
    { key: 'recognition', label: '🌟 Recognition Wall', count: recognitions.length },
  ];

  return (
    <div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">COMMUNITY HUB</div>
          <div className="page-sub">Announcements · Events · Recognition · Culture</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'announcements' && can('cwg') && (
            <button className="btn btn-primary" onClick={() => { setEditAnn(null); setAnnForm({ title: '', body: '', category: 'info', pinned: false }); setShowAnnForm(true); }}>+ ANNOUNCE</button>
          )}
          {tab === 'events' && can('cwg', 'stakeholder') && (
            <button className="btn btn-primary" onClick={() => setShowEvForm(true)}>+ CREATE EVENT</button>
          )}
          {tab === 'recognition' && myStatus.canGive && (
            <button className="btn btn-primary" onClick={() => setShowRecForm(true)}>🌟 RECOGNISE SOMEONE</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--brd2)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid var(--j1)' : '2px solid transparent', color: tab === t.key ? 'var(--j1)' : 'var(--txt2)', fontFamily: 'Rajdhani', fontSize: 13, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6, marginBottom: -1 }}>
            {t.label}
            {t.count > 0 && <span style={{ fontSize: 10, background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.25)', padding: '1px 6px', borderRadius: 8, color: 'var(--j1)' }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ── ANNOUNCEMENTS TAB ── */}
      {tab === 'announcements' && (
        <div>
          {announcements.length === 0 && <div className="empty-state">No announcements yet.</div>}
          {announcements.map(ann => {
            const cat = ANN_CATS[ann.category] || ANN_CATS.info;
            return (
              <div key={ann.id} className="card" style={{ marginBottom: 12, borderLeft: `3px solid ${cat.color}`, position: 'relative' }}>
                {ann.pinned && <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--j4)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>📌 PINNED</div>}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ fontSize: 24, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{cat.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>{ann.title}</span>
                      <span style={{ fontSize: 9, padding: '1px 6px', background: `${cat.color}18`, border: `1px solid ${cat.color}44`, borderRadius: 8, color: cat.color, fontFamily: 'Share Tech Mono', textTransform: 'uppercase' }}>{ann.category}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.6, marginBottom: 8 }}>{ann.body}</div>
                    <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>by {ann.authorName} · {timeAgo(ann.createdAt)}</div>
                  </div>
                  {can('cwg') && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button className="btn btn-sm" style={{ padding: '2px 7px', fontSize: 10, borderColor: ann.pinned ? 'var(--j4)' : 'var(--brd2)', color: ann.pinned ? 'var(--j4)' : 'var(--txt3)' }} onClick={() => togglePin(ann)} title={ann.pinned ? 'Unpin' : 'Pin'}>📌</button>
                      <button className="btn btn-sm" style={{ padding: '2px 7px', fontSize: 10 }} onClick={() => { setEditAnn(ann); setAnnForm({ title: ann.title, body: ann.body, category: ann.category, pinned: ann.pinned }); setShowAnnForm(true); }}>✏</button>
                      <button className="btn btn-sm btn-danger" style={{ padding: '2px 7px', fontSize: 10 }} onClick={() => deleteAnn(ann.id)}>✕</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── EVENTS TAB ── */}
      {tab === 'events' && (
        <div>
          {upcoming.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 10, color: 'var(--j5)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 12 }}>UPCOMING EVENTS</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {upcoming.map(ev => <EventCard key={ev.id} ev={ev} user={user} can={can} onRsvp={rsvp} onDelete={deleteEvent} />)}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginBottom: 12 }}>PAST EVENTS</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {past.map(ev => <EventCard key={ev.id} ev={ev} user={user} can={can} onRsvp={rsvp} onDelete={deleteEvent} past />)}
              </div>
            </div>
          )}
          {events.length === 0 && <div className="empty-state">No events scheduled yet. CWG will post upcoming events here.</div>}
        </div>
      )}

      {/* ── RECOGNITION WALL TAB ── */}
      {tab === 'recognition' && (
        <div>
          {/* Monthly status banner */}
          <div style={{ background: myStatus.canGive ? 'rgba(0,255,136,0.06)' : 'rgba(255,215,0,0.06)', border: `1px solid ${myStatus.canGive ? 'var(--j5)' : 'var(--j4)'}`, borderRadius: 4, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: myStatus.canGive ? 'var(--j5)' : 'var(--j4)', marginBottom: 3 }}>
                {myStatus.canGive ? '✓ You can recognise someone this month' : '✓ Recognition given this month'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--txt2)' }}>
                {myStatus.canGive
                  ? 'Pick 1 person who helped you or the team. They get +50 pts. You get +25 pts for sending.'
                  : myStatus.gave ? `You recognised ${myStatus.gave.receiverName}: "${myStatus.gave.message?.slice(0, 60)}…"` : 'Already used for this month.'}
              </div>
            </div>
            {myStatus.canGive && (
              <button className="btn btn-success" onClick={() => setShowRecForm(true)}>🌟 RECOGNISE</button>
            )}
          </div>

          {/* Recognition cards */}
          {recognitions.length === 0 && <div className="empty-state">No recognitions yet. Be the first to celebrate a colleague!</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {recognitions.map(r => (
              <div key={r.id} style={{ background: 'rgba(255,215,0,0.04)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 6, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#ffd700', fontFamily: 'Orbitron', flexShrink: 0 }}>{r.receiverAvatar || r.receiverName[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)' }}>{r.receiverName}</div>
                    <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>recognised by {r.giverName}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 800, color: '#ffd700' }}>+{r.points}</div>
                    <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>pts</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.5, fontStyle: 'italic', borderLeft: '2px solid rgba(255,215,0,0.3)', paddingLeft: 10, marginBottom: 8 }}>
                  "{r.message}"
                </div>
                <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{timeAgo(r.createdAt)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Announcement form modal ── */}
      {showAnnForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 540 }}>
            <button className="modal-close" onClick={() => { setShowAnnForm(false); setEditAnn(null); }}>×</button>
            <div className="modal-title">{editAnn ? 'EDIT ANNOUNCEMENT' : 'POST ANNOUNCEMENT'}</div>
            <div className="form-group"><label className="label">Title</label><input className="input" value={annForm.title} onChange={e => setAnnForm(p => ({ ...p, title: e.target.value }))} placeholder="Announcement headline…" /></div>
            <div className="form-group"><label className="label">Body</label><textarea className="input" rows={4} value={annForm.body} onChange={e => setAnnForm(p => ({ ...p, body: e.target.value }))} placeholder="Full message…" /></div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div className="form-group" style={{ flex: 1, margin: 0 }}>
                <label className="label">Category</label>
                <select className="input" value={annForm.category} onChange={e => setAnnForm(p => ({ ...p, category: e.target.value }))}>
                  <option value="info">ℹ Info</option>
                  <option value="urgent">🚨 Urgent</option>
                  <option value="event">📅 Event</option>
                  <option value="achievement">🏆 Achievement</option>
                </select>
              </div>
              <div className="form-group" style={{ margin: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                  <input type="checkbox" checked={annForm.pinned} onChange={e => setAnnForm(p => ({ ...p, pinned: e.target.checked }))} style={{ accentColor: 'var(--j4)', width: 14, height: 14 }} />
                  <span className="label" style={{ margin: 0 }}>Pin to top</span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => { setShowAnnForm(false); setEditAnn(null); }}>CANCEL</button>
              <button className="btn btn-primary" onClick={saveAnn} disabled={!annForm.title || !annForm.body}>{editAnn ? 'SAVE CHANGES' : 'POST'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Event form modal ── */}
      {showEvForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <button className="modal-close" onClick={() => setShowEvForm(false)}>×</button>
            <div className="modal-title">CREATE EVENT</div>
            <div className="form-group"><label className="label">Event Name</label><input className="input" value={evForm.title} onChange={e => setEvForm(p => ({ ...p, title: e.target.value }))} placeholder="Event title…" /></div>
            <div className="form-group"><label className="label">Description</label><textarea className="input" rows={3} value={evForm.description} onChange={e => setEvForm(p => ({ ...p, description: e.target.value }))} placeholder="What's happening, who should attend, why it matters…" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="label">Type</label>
                <select className="input" value={evForm.type} onChange={e => setEvForm(p => ({ ...p, type: e.target.value }))}>
                  {Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="label">Date & Time</label>
                <input type="datetime-local" className="input" value={evForm.date} onChange={e => setEvForm(p => ({ ...p, date: e.target.value }))} />
              </div>
            </div>
            <div className="form-group"><label className="label">Location <span style={{ color: 'var(--txt3)' }}>(optional)</span></label><input className="input" value={evForm.location} onChange={e => setEvForm(p => ({ ...p, location: e.target.value }))} placeholder="Office address, Zoom link, or Virtual" /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowEvForm(false)}>CANCEL</button>
              <button className="btn btn-primary" onClick={saveEvent} disabled={!evForm.title || !evForm.date}>CREATE EVENT</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recognition form modal ── */}
      {showRecForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <button className="modal-close" onClick={() => setShowRecForm(false)}>×</button>
            <div className="modal-title">🌟 RECOGNISE A COLLEAGUE</div>
            <div style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 4, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--txt2)' }}>
              You get <strong style={{ color: '#ffd700' }}>1 recognition to give per month</strong>. They receive <strong style={{ color: 'var(--j5)' }}>+50 pts</strong>. You earn <strong style={{ color: 'var(--j1)' }}>+25 pts</strong> for giving.
            </div>
            <div className="form-group">
              <label className="label">Who are you recognising?</label>
              <select className="input" value={recForm.receiverId} onChange={e => setRecForm(p => ({ ...p, receiverId: e.target.value }))}>
                <option value="">— Select a colleague —</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="label">Why are you recognising them? <span style={{ color: 'var(--txt3)' }}>(shown publicly)</span></label>
              <textarea className="input" rows={4} value={recForm.message} onChange={e => setRecForm(p => ({ ...p, message: e.target.value }))} maxLength={300} placeholder="Describe what they did that deserves recognition — specific is better than generic…" />
              <div style={{ fontSize: 9, color: 'var(--txt3)', textAlign: 'right', fontFamily: 'Share Tech Mono' }}>{recForm.message.length}/300</div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ borderColor: 'var(--brd2)', color: 'var(--txt2)' }} onClick={() => setShowRecForm(false)}>CANCEL</button>
              <button className="btn btn-success" onClick={sendRecognition} disabled={!recForm.receiverId || !recForm.message.trim()}>🌟 SEND RECOGNITION</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EventCard({ ev, user, can, onRsvp, onDelete, past }) {
  const type = EVENT_TYPES[ev.type] || EVENT_TYPES.event;
  const rsvpd = (ev.rsvps || []).some(r => r.userId === user?.id);
  const isPast = !!past;
  return (
    <div style={{ background: 'var(--bg2)', border: `1px solid ${isPast ? 'var(--brd2)' : type.color + '44'}`, borderRadius: 6, overflow: 'hidden', opacity: isPast ? 0.7 : 1 }}>
      <div style={{ height: 4, background: type.color, opacity: 0.7 }} />
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 9, padding: '1px 7px', background: type.color + '18', border: `1px solid ${type.color}44`, borderRadius: 8, color: type.color, fontFamily: 'Share Tech Mono', textTransform: 'uppercase', letterSpacing: 1 }}>{type.icon} {type.label}</span>
              {isPast && <span style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>PAST</span>}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', lineHeight: 1.3 }}>{ev.title}</div>
          </div>
          {can('cwg', 'stakeholder') && <button className="btn btn-sm btn-danger" style={{ padding: '2px 6px', fontSize: 10, flexShrink: 0 }} onClick={() => onDelete(ev.id)}>✕</button>}
        </div>

        {ev.description && <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.5, marginBottom: 10 }}>{ev.description}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--txt)', fontWeight: 600 }}>📅 {formatDate(ev.date)} at {formatTime(ev.date)}</div>
          {ev.location && <div style={{ fontSize: 11, color: 'var(--txt2)' }}>📍 {ev.location}</div>}
          <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Organised by {ev.organizerName}</div>
        </div>

        {/* RSVPs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ display: 'flex', gap: -4 }}>
              {(ev.rsvps || []).slice(0, 5).map((r, i) => (
                <div key={r.userId} title={r.userName} style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,212,255,0.15)', border: '1px solid var(--j1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'var(--j1)', marginLeft: i > 0 ? -6 : 0, zIndex: 5 - i, fontFamily: 'Orbitron' }}>
                  {r.userName[0]}
                </div>
              ))}
            </div>
            <span style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>
              {(ev.rsvps || []).length} going{(ev.rsvps || []).length > 5 ? ` (+${(ev.rsvps || []).length - 5})` : ''}
            </span>
          </div>
          {!isPast && (
            <button className="btn btn-sm" style={{ borderColor: rsvpd ? 'var(--j5)' : 'var(--brd2)', color: rsvpd ? 'var(--j5)' : 'var(--txt2)', background: rsvpd ? 'rgba(0,255,136,0.08)' : 'transparent', fontSize: 11 }} onClick={() => onRsvp(ev.id)}>
              {rsvpd ? '✓ I\'m Going' : 'RSVP'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
